import { FileText, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Panel } from "@/components/ui/Panel";
import { toast } from "sonner";
import type { FileResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { fileStatusLabel, isAwaitingPages } from "@/lib/file-status";
import { RetryAction } from "@/components/shared/RetryAction";

/** The supplied PDFs whose pages are not figures yet. */
function drawingsBeingPrepared(
  files: FileResponse[] | undefined,
): FileResponse[] {
  return (files ?? []).filter(
    (f) => f.file_type === "figure" && isAwaitingPages(f.processing_status),
  );
}

/**
 * What the attorney is shown between supplying a PDF and its pages arriving.
 *
 * A PDF is the one file at this surface that is not the figure it produces:
 * the server draws it into a figure per page, which takes seconds to minutes,
 * and until then the figure count says zero. Without a row here the upload
 * looks like it did nothing, and a refused file looks the same as a slow one.
 *
 * The rows leave as the pages arrive, since a prepared file is a set of figures
 * and is drawn as figures. A file that will not be prepared leaves by being
 * taken away, which is offered only once nothing is drawing it: a file removed
 * under a running render is the one case the write itself cannot see.
 */
export function PreparingDrawings({
  taskId,
  files,
  onRemove,
}: {
  taskId: string | undefined;
  files: FileResponse[] | undefined;
  onRemove?: (fileId: string) => void;
}) {
  const preparing = drawingsBeingPrepared(files);
  if (preparing.length === 0) return null;

  return (
    <ul data-testid="figures-preparing" className="mt-3 flex flex-col gap-1.5">
      {preparing.map((file) => (
        <Panel
          as="li"
          shape="row"
          pad="tight"
          key={file.file_id}
          data-testid={`preparing-row-${file.file_id}`}
          className="flex items-start gap-2.5 bg-muted/40"
        >
          {file.processing_status === "render_failed" ? (
            <FileText className="mt-0.5 size-4 flex-none text-destructive" />
          ) : (
            <Spinner className="mt-0.5 text-muted-foreground" />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate font-mono text-[11.5px]">
              {file.original_filename}
            </span>
            <span className="text-[11.5px] text-muted-foreground">
              {fileStatusLabel(file.processing_status)}
            </span>
            {/* The server's own sentence, which says what is wrong with this
                file and what to do about it. */}
            {file.processing_error !== null && (
              <span className="text-[11.5px] text-destructive">
                {file.processing_error}
              </span>
            )}
            {file.processing_status === "render_failed" &&
              onRemove !== undefined && (
                <div className="mt-1.5">
                  <Button
                    type="button"
                    size="dense"
                    variant="quiet"
                    className="h-7 cursor-pointer text-[11.5px]"
                    aria-label={`Remove ${file.original_filename}`}
                    onClick={() => {
                      onRemove(file.file_id);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                    Remove
                  </Button>
                </div>
              )}
            {file.can_retry && taskId !== undefined && (
              <div className="mt-1.5">
                <RetryAction
                  taskId={taskId}
                  fileId={file.file_id}
                  filename={file.original_filename}
                  whenRefused={`Could not prepare ${file.original_filename} again.`}
                  onError={(message) => {
                    if (message !== null) toast.error(message);
                  }}
                />
              </div>
            )}
          </div>
        </Panel>
      ))}
    </ul>
  );
}
